import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { products } from '../data/mock';

export const ProductSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % products.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + products.length) % products.length);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const currentProduct = products[currentIndex];

  return (
    <section className="py-20 bg-gradient-to-br from-orange-50 via-white to-orange-50 overflow-hidden relative">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-[#D8461D]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-200/20 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#D8461D] to-orange-600 rounded-full mb-4">
            <Sparkles size={20} className="text-white" />
            <span className="text-white font-semibold">Explore Our Collection</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Premium Product Range
          </h2>
          <p className="text-xl text-slate-600">
            Quality you can trust, diversity you need
          </p>
        </div>

        <div className="relative">
          {/* Slider Container */}
          <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-white to-slate-50">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Image Side */}
              <div className="relative h-[400px] md:h-[550px] bg-gradient-to-br from-slate-50 to-orange-50 flex items-center justify-center p-12 md:p-16">
                <div className="absolute inset-0 bg-gradient-to-br from-[#D8461D]/5 to-transparent"></div>
                <img
                  src={currentProduct.usageImage || currentProduct.image}
                  alt={`${currentProduct.name} in use`}
                  className="max-h-full max-w-full object-cover transition-all duration-700 transform hover:scale-105 relative z-10 rounded-xl shadow-2xl"
                />
                {/* Decorative circle */}
                <div className="absolute top-10 right-10 w-32 h-32 bg-gradient-to-br from-[#D8461D]/10 to-orange-200/10 rounded-full blur-2xl"></div>
                <div className="absolute bottom-10 left-10 w-40 h-40 bg-gradient-to-br from-orange-300/10 to-[#D8461D]/10 rounded-full blur-2xl"></div>
              </div>

              {/* Content Side */}
              <div className="flex flex-col justify-center p-8 md:p-12 lg:p-16 bg-gradient-to-br from-white via-slate-50 to-orange-50/30">
                <div className="space-y-6">
                  <div className="inline-block">
                    <span className="px-5 py-2 bg-gradient-to-r from-[#D8461D] to-orange-600 text-white rounded-full text-sm font-bold shadow-lg">
                      {currentProduct.category}
                    </span>
                  </div>
                  
                  <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                    {currentProduct.name}
                  </h3>
                  
                  <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
                    {currentProduct.description}
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <Link to={`/products/${currentProduct.id}`} className="flex-1">
                      <button className="w-full px-8 py-4 bg-gradient-to-r from-[#D8461D] to-orange-600 hover:from-[#b83817] hover:to-[#D8461D] text-white font-bold rounded-xl transition-all duration-300 hover:shadow-xl hover:scale-105 transform">
                        View Details
                      </button>
                    </Link>
                    <Link to="/products" className="flex-1">
                      <button className="w-full px-8 py-4 border-3 border-[#D8461D] text-[#D8461D] hover:bg-orange-50 font-bold rounded-xl transition-all duration-300 hover:shadow-lg">
                        All Products
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-4 md:left-6 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-gradient-to-br from-white to-slate-50 hover:from-[#D8461D] hover:to-orange-600 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110 z-20 group"
              aria-label="Previous slide"
            >
              <ChevronLeft size={28} className="text-[#D8461D] group-hover:text-white transition-colors" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 md:right-6 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-gradient-to-br from-white to-slate-50 hover:from-[#D8461D] hover:to-orange-600 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110 z-20 group"
              aria-label="Next slide"
            >
              <ChevronRight size={28} className="text-[#D8461D] group-hover:text-white transition-colors" />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-3 mt-8">
            {products.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-gradient-to-r from-[#D8461D] to-orange-600 w-10 shadow-lg'
                    : 'bg-slate-300 hover:bg-slate-400 w-3'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};